import { formatMoney } from '@/utils/format';
import type { BulkRecipient } from '@/types/portal';

interface PoolSweepPanelProps {
  rows: BulkRecipient[];
  currency: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
}

export default function PoolSweepPanel({
  rows,
  currency,
  accountName,
  accountNumber,
  bankName,
}: PoolSweepPanelProps) {
  const swept = rows.filter((row) => row.advance);
  if (swept.length === 0) return null;

  const sweepTotal = swept.reduce((sum, row) => sum + (row.advance?.amount ?? 0), 0);

  return (
    <section className="rounded-lg border border-secondary-200 bg-secondary-50/70">
      <header className="flex items-start gap-3 border-b border-secondary-200 px-5 py-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-base text-background-50">
          <i className="ri-hand-coin-line" />
        </span>
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Early payout reconciliation
          </h3>
          <p className="mt-0.5 text-xs text-secondary-900">
            {swept.length} creator{swept.length === 1 ? '' : 's'} borrowed against their invoice.
            Only the remaining balance is paid out, and{' '}
            <strong className="font-semibold">{formatMoney(sweepTotal, currency)}</strong> is
            auto-swept to the Twiva Pool Account.
          </p>
        </div>
      </header>

      <ul className="divide-y divide-secondary-200">
        {swept.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground-950">{row.name}</p>
              <p className="text-xs text-foreground-500">
                {row.advance?.percent}% advance · {row.advance?.reference} ·{' '}
                {row.advance?.advancedAt}
              </p>
            </div>
            <div className="flex items-center gap-4 text-right text-xs">
              <span>
                <span className="block text-foreground-500">Swept to pool</span>
                <span className="block font-medium text-secondary-900">
                  {formatMoney(row.advance?.amount ?? 0, currency)}
                </span>
              </span>
              <span className="hidden h-8 w-px bg-secondary-200 sm:block" />
              <span>
                <span className="block text-foreground-500">Paid to creator</span>
                <span className="block font-medium text-accent-700">
                  {formatMoney(row.amount - (row.advance?.amount ?? 0), currency)}
                </span>
              </span>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-secondary-200 px-5 py-3 text-xs text-secondary-900">
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-bank-line" />
          {accountName}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-hashtag" />
          {accountNumber}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-building-2-line" />
          {bankName}
        </span>
      </div>
    </section>
  );
}